import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  useToast,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";

const validationSchema = Yup.object({
  username: Yup.string().required("Username is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .required("Password is required"),
});

interface AddRestaurantAdminDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  getData: () => void;
  editData?: any;
}

const AddRestaurantAdminDrawer: React.FC<AddRestaurantAdminDrawerProps> = ({
  isOpen,
  onClose,
  getData,
  editData,
}) => {
  const toast = useToast();
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const initialValues: any = {
    restaurantName: editData ? editData?.restaurantName : "",
    username: editData ? editData?.userName : "",
    password: editData ? editData?.password : "",
    restaurantId: editData ? editData?._id : "",
  };

  const submitForm = (values: any, actions: any) => {
    // Extract only the fields we need to submit
    const submitData = {
      restaurantId: values.restaurantId,
      username: values.username,
      password: values.password,
    };

    if (editData && editData._id) {
      axios({
        method: "patch",
        url: `${process.env.REACT_APP_BASE_URL}/admin/restaurant-admin`,
        data: submitData,
      })
        .then((response) => {
          actions.setSubmitting(false);
          onClose();
          getData();
          toast({
            title: "Restaurant Admin updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to update Restaurant Admin",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          console.error("API Error:", error);
        });
    } else {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_BASE_URL}/admin/subadmin`,
        data: submitData,
      })
        .then((response) => {
          actions.setSubmitting(false);
          onClose();
          getData();
          toast({
            title: "Restaurant Admin added successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to add Restaurant Admin",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          console.error("API Error:", error);
        });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitForm}
    >
      {(props) => (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader borderBottomWidth="1px" bg="#FFF1F1" color="#000">
              {editData ? "Edit Restaurant Admin" : "Add Restaurant Admin"}
            </DrawerHeader>
            <Form
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DrawerBody
                flex="1 1 auto"
                overflowY="auto"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                <VStack
                  spacing={6}
                  pt={4}
                  height="65vh"
                  align="stretch"
                  overflowY={"scroll"}
                  paddingX={3}
                >
                  {/* Hidden restaurantId field */}
                  <Field name="restaurantId" type="hidden" />

                  {/* Restaurant Name - Readonly */}
                  <Field name="restaurantName">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl>
                        <FormLabel>Restaurant Name</FormLabel>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Restaurant name"
                          isReadOnly={true}
                          bg="gray.100"
                        />
                      </FormControl>
                    )}
                  </Field>

                  {/* Username */}
                  <Field name="username">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.username && form.touched.username
                        }
                      >
                        <FormLabel>Username</FormLabel>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter username"
                        />
                        <FormErrorMessage>
                          {form.errors.username}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Password */}
                  <Field name="password">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.password && form.touched.password
                        }
                      >
                        <FormLabel>Password</FormLabel>
                        <InputGroup size="md">
                          <Input
                            {...field}
                            type={show ? "text" : "password"}
                            placeholder="Enter password"
                            autoComplete="new-password"
                          />
                          <InputRightElement width="4.5rem">
                            <Box cursor={"pointer"} onClick={handleClick}>
                              {show ? <IoEyeOffOutline /> : <IoEyeOutline />}
                            </Box>
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </DrawerBody>

              <DrawerFooter
                borderTopWidth="1px"
                flex="0 0 auto"
                bg="white"
                position="sticky"
                bottom={0}
                zIndex={1}
              >
                <Button variant="outline" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={props.isSubmitting}
                  type="submit"
                >
                  {editData ? "Update" : "Add"} Restaurant Admin
                </Button>
              </DrawerFooter>
            </Form>
          </DrawerContent>
        </Drawer>
      )}
    </Formik>
  );
};

export default AddRestaurantAdminDrawer;
